import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { fetchMenuItemsRequest,setSelectedCategory } from '../redux/slices/menuItemsSlice';
import { clearCart } from '../redux/slices/cartSlice';
import { replaceMenuWidgetType, updateSiteContentRequest } from '../redux/slices/siteContentSlice';
import { fetchPaymentStatusRequest, clearPaymentStatusState } from '../redux/slices/orderSlice';
import { fetchCategoriesRequest } from '../redux/slices/categoriesSlice';
import { fetchModifiersRequest } from '../redux/slices/modifiersSlice';
import { useMenuItemActions } from '../hooks/useMenuItemActions';
import { filterAndSortMenuItems } from '../utils/menuFilters';
import { FiLayout } from 'react-icons/fi';
import {
  StatusMessage,
  MenuGrid,
  CategoryFilter,
  MenuSearch,
  ItemModifierModal,
} from '../components';
import { RepeatCustomisationModal, DeleteVariantModal } from '../components/modifiers';
import MenuStyleSelectionModal from '../components/MenuStyleSelectionModal';
import { MenuItemCardVariant } from '../components/MenuGrid';
import { PaymentSuccessPopup, PaymentFailedProcessingPopup } from '../components/checkout';
import { fetchTpnConfigRequest } from '../redux/slices/tpnConfigSlice';
import { useTpnCart } from '../hooks/useTpnCart';
import { useNavSpacing } from '../hooks/useNavSpacing';
import { useHeroBarHeight } from '../hooks/useHeroBarHeight';
import { getAppMode } from '../utils/appConfig';

const MenuPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const appNavigate = useAppNavigate();
  const { id } = useParams<{ id: string }>();
  const { loading, error, items: menuData, selectedCategory } = useAppSelector(state => state.menuItems);
  const { data: siteContentData, updateLoading } = useAppSelector(state => state.siteContent);
  const { isEditControl } = useAppSelector(state => state.editControl);
  const { paymentStatusResponse, paymentStatusError } = useAppSelector(state => state.order);
  const { selectedRestaurant } = useAppSelector(state => state.restaurant);
  const { items: cartItems } = useAppSelector(state => state.cart);
  const shouldShowCart = useTpnCart(true);

  // Track if we're saving menu style to close modal after success
  const isSavingMenuStyle = useRef(false);
  const prevUpdateLoading = useRef<boolean>(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [showPaymentFailedPopup, setShowPaymentFailedPopup] = useState(false);
  const [showPaymentSuccessPopup, setShowPaymentSuccessPopup] = useState(false);
  const [showWidgetModal, setShowWidgetModal] = useState(false);

  // Use reusable navigation spacing hook
  const navSpacing = useNavSpacing();
  
  // Use custom hook for hero bar height (follows React best practices)
  const heroBarHeight = useHeroBarHeight();

  // 🛒 Use centralized menu item actions hook
  const {
    isModifierModalOpen,
    isRepeatCustomisationModalOpen,
    isDeleteVariantModalOpen,
    currentMenuItem,
    selectedCartItem,
    editMode,
    deleteVariants,
    handleAddItemClick,
    handleMenuItemIncrement,      // NEW: For menu cards
    handleMenuItemDecrement,      // NEW: For menu cards
    handleRepeatCustomisation,
    handleChooseNewCustomisation,
    handleAddToCartFromModal,
    handleUpdateCartItem,
    handleDeleteVariant,
    closeModifierModal,
    closeRepeatCustomisationModal,
    closeDeleteVariantModal,
  } = useMenuItemActions();

  // Handle menu style selection - save and close after API success
  const handleSelectWidget = useCallback((widgetType: string) => {
    // Mark that we're saving to close modal after API completes
    isSavingMenuStyle.current = true;
    // Update Redux
    dispatch(replaceMenuWidgetType(widgetType));
    // Save to API
    dispatch(updateSiteContentRequest());
    // DON'T close modal here - let useEffect close it after API success
  }, [dispatch]);

  // Close modal after API successfully saves menu style
  useEffect(() => {
    // Only close when loading transitions from true → false
    if (isSavingMenuStyle.current && prevUpdateLoading.current && !updateLoading) {
      setShowWidgetModal(false);
      isSavingMenuStyle.current = false;
    }
    // Track previous loading state
    prevUpdateLoading.current = updateLoading;
  }, [updateLoading]);

  // Dynamic cardVariant based on Redux menuWidgetTypes
  const cardVariant = useMemo((): MenuItemCardVariant => {
    if (!siteContentData?.menuWidgetTypes || siteContentData.menuWidgetTypes.length === 0) {
      return 'modern'; // default
    }

    const menuType = siteContentData.menuWidgetTypes[0]; // Get first menu widget type

    // Map widget types to card variants
    if (menuType.includes('Basic')) return 'basic';
    if (menuType.includes('Classic')) return 'classic';
    if (menuType.includes('Prime')) return 'prime';
    return 'modern'; // default for Modern or unknown types
  }, [siteContentData?.menuWidgetTypes]);

  // Hybrid payment verification - URL params first, then API fallback
  useEffect(() => {
    // Skip ALL payment verification in edit/preview modes
    const appMode = getAppMode();
    if (appMode === 'edit' || appMode === 'preview') {
      console.log('🚫 Skipping payment verification in', appMode, 'mode');
      return;
    }
    
    console.log('🔍 MenuPage loaded with URL:', window.location.href);
    console.log('🔍 Restaurant ID from URL:', id);
    
    const searchParams = new URLSearchParams(window.location.search);
    const responseCode = searchParams.get('responseCode');
    const transactionId = searchParams.get('transactionId');
    const transactionReferenceId = searchParams.get('transactionReferenceId');
    const paymentStatus = searchParams.get('payment_status');

    console.log('🔍 Payment parameters found:', {
      responseCode,
      transactionId,
      transactionReferenceId,
      paymentStatus,
      hasAnyPaymentParam: !!(responseCode || transactionId || transactionReferenceId || paymentStatus)
    });

    // Option 1: Check URL parameters first (old logic - fast, no API call)
    if (responseCode === '200' || paymentStatus?.toLowerCase() === 'success') {
      console.log('✅ Payment success from URL parameters');
      setShowPaymentSuccessPopup(true);
    } else if (responseCode === '400') {
      console.log('❌ Payment failed from URL parameters:', responseCode);
      setShowPaymentFailedPopup(true);
    }
    // Option 1.5: Verify transactionId or transactionReferenceId via API (Square/other payments)
    else if ((transactionId || transactionReferenceId) && selectedRestaurant?.restaurant_id) {
      console.log('🔍 Transaction ID found - verifying via API');
      dispatch(fetchPaymentStatusRequest({ 
        order_id: transactionId || transactionReferenceId || '',
        restaurant_id: selectedRestaurant.restaurant_id 
      }));
    }
    // Option 2: No responseCode but restaurant ID exists - verify via API (new logic - secure)
    else if (id && selectedRestaurant?.restaurant_id) {
      console.log('💳 No responseCode found - calling API to verify payment status');
      console.log('🏪 Using restaurant_id from Redux:', selectedRestaurant.restaurant_id);
      dispatch(fetchPaymentStatusRequest({ 
        order_id: id,
        restaurant_id: selectedRestaurant.restaurant_id 
      }));
    } else {
      console.log('🔍 No payment parameters or ID found in URL');
    }
  }, [id, selectedRestaurant, dispatch]);

  // Handle payment status API response
  useEffect(() => {
    if (paymentStatusResponse) {
      console.log('📥 Payment status response received:', paymentStatusResponse);
      
      // Only show popup if 'status' key exists in response
      if ('status' in paymentStatusResponse) {
        // Check status field (boolean) or payment_status field (string)
        const isSuccess = paymentStatusResponse.status === true || 
                          paymentStatusResponse.payment_status === 'success';
        
        if (isSuccess) {
          console.log('✅ Payment verified as successful');
          setShowPaymentSuccessPopup(true);
        } else {
          console.log('❌ Payment failed or invalid');
          setShowPaymentFailedPopup(true);
        }
      } else {
        console.log('⚠️ No status key in response - skipping popup');
      }
      
      // Clear payment status state after handling
      dispatch(clearPaymentStatusState());
    }
  }, [paymentStatusResponse, dispatch]);

  // Handle payment status API errors
  useEffect(() => {
    if (paymentStatusError) {
      console.error('❌ Payment status API error:', paymentStatusError);
      setShowPaymentFailedPopup(true);
      
      // Clear payment status state after handling
      dispatch(clearPaymentStatusState());
    }
  }, [paymentStatusError, dispatch]);
  
  // Fetch menu data on component mount
  useEffect(() => {
    dispatch(fetchMenuItemsRequest())
    dispatch(fetchTpnConfigRequest())
    dispatch(fetchCategoriesRequest());
    dispatch(fetchModifiersRequest());
  }, [dispatch]);

  const handleCategoryChange = useCallback((category: string | null) => {
    dispatch(setSelectedCategory(category));
  }, [dispatch]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);


  // Filter and sort menu items using utility
  const filteredAndSortedMenuItems = useMemo(() => {
    return filterAndSortMenuItems(menuData, selectedCategory, searchQuery);
  }, [menuData, selectedCategory, searchQuery]);


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 md:pt-36 lg:pt-40">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-md p-6">
                  <div className="h-48 bg-gray-300 rounded mb-4"></div>
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <StatusMessage message={`Error Loading Menu: ${error}`} type="error" />;
  }

  if (!menuData || menuData.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 md:pt-36 lg:pt-40">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🍽️</div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">No Menu Available</h2>
            <p className="text-gray-600">
              Menu items are not available at the moment. Please check back later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-white" style={{ paddingTop: `${navSpacing}px` }}>
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Title and Search Section */}
          <div className="mb-6">
            {/* Title - Always at top on mobile, desktop: same row with search */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
              {/* Title */}
              <h1 className="text-3xl md:text-4xl font-bold">Our Menu</h1>
              
              {/* Search - Hidden on mobile, shown on desktop in header */}
              <div className="hidden md:flex items-center gap-4 flex-1 justify-end">
                {/* Search Bar */}
                <div className="w-full max-w-md">
                  <MenuSearch
                    searchQuery={searchQuery}
                    onSearchChange={handleSearchChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Search - Above Category Filter */}
          <div className="md:hidden mb-4">
            <MenuSearch
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
            />
          </div>

          {/* Category Filter - Sticky below nav */}
          <div 
            className="sticky z-[45] -mx-4 px-4 mb-8 bg-white"
            style={{ top: `${navSpacing - heroBarHeight}px` }}
          >
            <CategoryFilter
              menuItems={menuData}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
            />
          </div>

          {/* Menu Items Grid */}
          {filteredAndSortedMenuItems.length > 0 ? (
            <MenuGrid
              menuItems={filteredAndSortedMenuItems}
              selectedCategory={null}
              searchQuery=""
              variant={cardVariant}
              onItemClick={handleAddItemClick}
              onAddToCart={shouldShowCart ? handleAddItemClick : undefined}
              cartItems={cartItems}
              onIncrementQuantity={handleMenuItemIncrement}
              onDecrementQuantity={handleMenuItemDecrement}
            />
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🍽️</div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">No Items Found</h2>
              <p className="text-gray-600">
                {searchQuery ? 'No items match your search.' : 'No menu items are currently available.'}
              </p>
            </div>
          )}

          {/* Select Menu Card Style Button - Floating - Only show when edit control is enabled */}
          {isEditControl && (
            <button
              onClick={() => setShowWidgetModal(true)}
              className="fixed bottom-6 right-6 px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 z-40"
              style={{ backgroundColor: '#EE4446' }}
              aria-label="Select menu card style"
            >
              <FiLayout className="w-5 h-5 text-white" />
              <span className="text-white font-medium text-sm whitespace-nowrap">Select Menu Card Style</span>
            </button>
          )}
        </div>
      </div>

      {/* Payment Popups */}
      <PaymentFailedProcessingPopup
        isOpen={showPaymentFailedPopup}
        onClose={() => {
          setShowPaymentFailedPopup(false);
          appNavigate('/menu'); // Navigate to /menu (removes restaurant ID)
        }}
        onTryAgain={() => {
          setShowPaymentFailedPopup(false);
          appNavigate('/menu'); // Navigate to /menu (removes restaurant ID)
        }}
      />
      <PaymentSuccessPopup
        isOpen={showPaymentSuccessPopup}
        onClose={() => {
          setShowPaymentSuccessPopup(false);
          appNavigate('/menu'); // Navigate to /menu (removes restaurant ID)
        }}
        onContinue={() => {
          setShowPaymentSuccessPopup(false);
          dispatch(clearCart());
          appNavigate('/menu'); // Navigate to /menu (removes restaurant ID)
        }}
      />

      {/* Menu Style Selection Modal */}
      <MenuStyleSelectionModal
        isOpen={showWidgetModal}
        onClose={() => setShowWidgetModal(false)}
        onSelectStyle={handleSelectWidget}
        isLoading={updateLoading}
      />

      {/* Global Modals from useMenuItemActions */}
      <ItemModifierModal
        isOpen={isModifierModalOpen}
        onClose={closeModifierModal}
        onAddToCart={editMode ? handleUpdateCartItem : handleAddToCartFromModal}
        initialModifiers={editMode ? selectedCartItem?.selected?.modifiers : undefined}
        initialQuantity={editMode ? selectedCartItem?.quantity : 1}
        editMode={editMode}
      />

      <RepeatCustomisationModal
        isOpen={isRepeatCustomisationModalOpen}
        itemName={currentMenuItem?.name || ''}
        modifiers={selectedCartItem?.selected?.modifiers || []}
        onRepeat={handleRepeatCustomisation}
        onChoose={handleChooseNewCustomisation}
        onClose={closeRepeatCustomisationModal}
      />

      <DeleteVariantModal
        isOpen={isDeleteVariantModalOpen}
        itemName={currentMenuItem?.name || ''}
        variants={deleteVariants}
        onUpdateQuantity={handleDeleteVariant}
        onClose={closeDeleteVariantModal}
      />
    </>
  );
};

export default MenuPage;
